import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import z from 'zod';
import { useSelector } from 'react-redux';
import { PersonalKycSchema } from '../../../schemas/storeGetStartedSchema';
import { usePageLoader } from '../../../context/pageLoader';
import useIonicToast from '../../../hooks/useIonicToast';
import { RootState } from '../../../store/store';
import { saveUserKycDocuments } from '../saveUserKycDocuments';

type FormShape = z.infer<typeof PersonalKycSchema>;

export default function usePersonalKycService() {
  const queryClient = useQueryClient();
  const { setBusy } = usePageLoader();
  const { presentIonToast } = useIonicToast();

  const { signedAccessToken: authToken } = useSelector(
    (state: RootState) => state.userSession
  );

  const methods = useForm<FormShape>({
    resolver: zodResolver(PersonalKycSchema),
    mode: 'all',
  });

  const mutation = useMutation({
    mutationFn: async () => {
      setBusy(true);
      if (!authToken) throw new Error('Authentication required.');

      const values = methods.getValues();
      const dirty = methods.formState.dirtyFields as Partial<
        Record<keyof FormShape, boolean>
      >;

      await saveUserKycDocuments({ body: values, authToken, dirty });

      presentIonToast('Personal verification documents submitted.');
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      methods.reset();
    },
    onError: (error: any) => {
      presentIonToast(error?.message || 'Failed to save documents.', 'error');
    },
    onSettled: () => setBusy(false),
  });

  /**
   * Submit-time validation: only fields the user touched get checked.
   * - NIN: file + document number must both be present together.
   * - BVN: 11 digits, number only.
   * - UTILITY_BILL: file required (address is optional metadata).
   */
  const onSave = async () => {
    const values = methods.getValues();
    const dirty = methods.formState.dirtyFields as Partial<
      Record<keyof FormShape, boolean>
    >;

    methods.clearErrors([
      'NIN_NUMBER',
      'NIN_FILE',
      'BVN_NUMBER',
      'UTILITY_BILL_FILE',
      'UTILITY_BILL_ADDRESS',
    ]);

    let hasError = false;

    if (dirty.NIN_FILE || dirty.NIN_NUMBER) {
      if (!(values.NIN_FILE instanceof File)) {
        methods.setError('NIN_FILE', {
          message: 'Please upload your NIN slip',
        });
        hasError = true;
      }
      if (!values.NIN_NUMBER?.trim()) {
        methods.setError('NIN_NUMBER', { message: 'NIN number is required' });
        hasError = true;
      } else if (!/^\d{11}$/.test(values.NIN_NUMBER.trim())) {
        methods.setError('NIN_NUMBER', { message: 'NIN must be 11 digits' });
        hasError = true;
      }
    }

    if (dirty.BVN_NUMBER) {
      const bvn = values.BVN_NUMBER?.trim();
      if (!bvn) {
        methods.setError('BVN_NUMBER', { message: 'BVN is required' });
        hasError = true;
      } else if (!/^\d{11}$/.test(bvn)) {
        methods.setError('BVN_NUMBER', { message: 'BVN must be 11 digits' });
        hasError = true;
      }
    }

    if (dirty.UTILITY_BILL_FILE || dirty.UTILITY_BILL_ADDRESS) {
      if (!(values.UTILITY_BILL_FILE instanceof File)) {
        methods.setError('UTILITY_BILL_FILE', {
          message: 'Please upload your utility bill',
        });
        hasError = true;
      }
    }

    if (hasError) return;

    const anyDirty = Boolean(
      dirty.NIN_FILE ||
      dirty.NIN_NUMBER ||
      dirty.BVN_NUMBER ||
      dirty.UTILITY_BILL_FILE ||
      dirty.UTILITY_BILL_ADDRESS
    );
    if (!anyDirty) {
      presentIonToast('Fill in at least one document to submit.', 'error');
      return;
    }

    await mutation.mutateAsync();
  };

  return { methods, onSave };
}
